import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageSquare, Send, Clock, AlertCircle, CheckCircle2, AtSign, Bell } from "lucide-react";
import { toast } from "sonner";

const SETTING_DEFS = [
  {
    key: "reassign_email",
    label: "Email on reassignment",
    description: "Email a board member when an admin assigns them a contact.",
    icon: Mail,
  },
  {
    key: "reassign_sms",
    label: "SMS on reassignment",
    description: "Text a board member when assigned (requires their phone number).",
    icon: MessageSquare,
  },
  {
    key: "mention_email",
    label: "Email on @mention in notes",
    description: "Email a board member when a teammate @tags them in a contact's notes.",
    icon: AtSign,
  },
  {
    key: "reminder_email",
    label: "Email when a follow-up is due",
    description: "Email the owner of a reminder on the day it's due.",
    icon: Bell,
  },
  {
    key: "csv_upload_email",
    label: "Email admins on CSV upload",
    description: "Notify all admins when new contacts are imported.",
    icon: Send,
  },
  {
    key: "contacted_email_admin",
    label: "Email admins when a contact is closed",
    description: "Get a heads-up when a board member marks a contact as contacted.",
    icon: CheckCircle2,
  },
  {
    key: "weekly_digest",
    label: "Weekly Monday digest",
    description: "Email each board member a list of their open claimed contacts.",
    icon: Clock,
  },
];

const STATUS_COLORS = {
  queued: "#EAB308",
  sent: "#4A5D23",
  failed: "#B91C1C",
};

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function NotificationsAdmin() {
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [digestRunning, setDigestRunning] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/notifications/settings"),
        api.get("/notifications", { params: { limit: 100 } }),
      ]);
      setSettings(s);
      setLog(l);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (key, value) => {
    setBusy(true);
    setSettings((s) => ({ ...s, [key]: value }));
    try {
      const { data } = await api.patch("/notifications/settings", { [key]: value });
      setSettings(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
      load();
    } finally {
      setBusy(false);
    }
  };

  const runDigest = async () => {
    setDigestRunning(true);
    try {
      const { data } = await api.post("/notifications/digest/run");
      toast.success(`Digest queued for ${data.sent} member(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setDigestRunning(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="notifications-admin">
      {/* Provider banner */}
      <div className="border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-3 rounded-sm">
        <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-900">
          <span className="font-medium">Provider not yet configured.</span>{" "}
          Notifications are being <span className="font-mono text-xs bg-amber-100 px-1.5 py-0.5">queued</span>{" "}
          and logged below. Once you connect <span className="font-mono">jubileeboard.com</span> with Resend
          (email) and Twilio (SMS), they'll start delivering automatically.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight">Settings</h3>
            <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
              admin only
            </span>
          </div>
          <div className="border border-stone-300 bg-white divide-y divide-stone-200">
            {SETTING_DEFS.map(({ key, label, description, icon: Icon }) => (
              <label
                key={key}
                className="flex items-start gap-3 p-4 cursor-pointer hover:bg-stone-50"
                data-testid={`setting-${key}`}
              >
                <div className="h-8 w-8 grid place-items-center bg-[var(--surface)] flex-shrink-0">
                  <Icon className="h-4 w-4 text-[#B85C38]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900">{label}</div>
                  <div className="text-xs text-stone-500 mt-0.5 leading-relaxed">{description}</div>
                </div>
                <Switch
                  checked={!!settings[key]}
                  onCheckedChange={(v) => toggle(key, v)}
                  disabled={busy}
                  data-testid={`switch-${key}`}
                />
              </label>
            ))}
          </div>

          <Button
            onClick={runDigest}
            disabled={digestRunning || !settings.weekly_digest}
            className="w-full rounded-sm bg-[#1C1917] hover:bg-[#292524] text-white"
            data-testid="run-digest-btn"
          >
            <Send className="h-4 w-4 mr-1.5" />
            {digestRunning ? "Running…" : "Run weekly digest now"}
          </Button>
          {!settings.weekly_digest && (
            <p className="text-[11px] font-mono text-stone-500 text-center">
              Enable the digest setting to run it manually.
            </p>
          )}
        </div>

        {/* Activity Log */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight">Recent Notifications</h3>
            <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
              {log.length} entries
            </span>
          </div>

          {log.length === 0 ? (
            <div className="border border-dashed border-stone-300 bg-white p-12 text-center">
              <p className="text-sm text-stone-500">
                No notifications yet. Reassign a contact or upload a CSV to see one queued here.
              </p>
            </div>
          ) : (
            <div className="border border-stone-300 bg-white divide-y divide-stone-200 max-h-[640px] overflow-y-auto">
              {log.map((n) => {
                const ChIcon = n.channel === "email" ? Mail : MessageSquare;
                const color = STATUS_COLORS[n.status] || "#57534E";
                return (
                  <div key={n.id} className="p-4 flex gap-3" data-testid={`notif-${n.id}`}>
                    <div className="h-8 w-8 grid place-items-center bg-[var(--surface)] flex-shrink-0">
                      <ChIcon className="h-4 w-4 text-stone-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-stone-900">
                          {n.subject || (n.channel === "sms" ? "SMS" : "Email")}
                        </span>
                        <Badge
                          className="rounded-full font-mono text-[10px] uppercase tracking-wider border"
                          style={{ backgroundColor: `${color}1A`, color, borderColor: `${color}55` }}
                        >
                          {n.status}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="rounded-sm font-mono text-[10px] uppercase tracking-wider border-stone-300 text-stone-600"
                        >
                          {n.event_type}
                        </Badge>
                      </div>
                      <div className="text-xs text-stone-600 mt-1">
                        to <span className="font-mono">{n.to || "—"}</span>{" "}
                        · <span className="font-mono">{formatTime(n.created_at)}</span>
                      </div>
                      {n.body && (
                        <pre className="mt-2 text-[11px] font-mono text-stone-600 bg-stone-50 border border-stone-200 p-2 rounded-sm whitespace-pre-wrap line-clamp-4 overflow-hidden max-h-20">
                          {n.body}
                        </pre>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
